import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import PwaManager from "@/components/PwaManager";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Units from "@/pages/Units";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Support from "@/pages/Support";
import Help from "@/pages/Help";
import Integrations from "@/pages/Integrations";
import Checklists from "@/pages/Checklists";
import ChecklistEditor from "@/pages/ChecklistEditor";
import ChecklistRunner from "@/pages/ChecklistRunner";
import Billing from "@/pages/Billing";
import Executions from "@/pages/Executions";
import ExecutionGo from "@/pages/ExecutionGo";
import Occurrences from "@/pages/Occurrences";
import Schedules from "@/pages/Schedules";
import Rules from "@/pages/Rules";
import Notifications from "@/pages/Notifications";
import APPCC from "@/pages/APPCC";
import Audits from "@/pages/Audits";
import Equipment from "@/pages/Equipment";
import Reports from "@/pages/Reports";
import Chatbot from "@/pages/Chatbot";
import Sanitary from "@/pages/Sanitary";
import Corporate from "@/pages/Corporate";
import TimeClock from "@/pages/TimeClock";
import TimeClockAdmin from "@/pages/TimeClockAdmin";
import TimeMirror from "@/pages/TimeMirror";
import Kiosk from "@/pages/Kiosk";

import AdminCompanies from "@/pages/AdminCompanies";
import AdminPlans from "@/pages/AdminPlans";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminPayments from "@/pages/AdminPayments";
import AdminMetrics from "@/pages/AdminMetrics";
import AdminTickets from "@/pages/AdminTickets";
import AdminAudit from "@/pages/AdminAudit";
import AdminFeatureFlags from "@/pages/AdminFeatureFlags";
import AdminMonitoring from "@/pages/AdminMonitoring";
import AdminLeads from "@/pages/AdminLeads";
import AdminSupportSessions from "@/pages/AdminSupportSessions";
import StockConsumptions from "@/pages/StockConsumptions";
import Trainings from "@/pages/Trainings";
import TeamRanking from "@/pages/TeamRanking";

// FASE 17 — role groups for route-level guards
const MANAGE = ["super_admin", "company_admin", "manager", "supervisor"];
const ADMIN_ONLY = ["super_admin", "company_admin"];

function AppRouter() {
  const location = useLocation();

  // Handle Emergent OAuth callback synchronously before any other routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />

      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/units"
        element={
          <ProtectedRoute roles={ADMIN_ONLY}>
            <Units />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute roles={ADMIN_ONLY}>
            <Support />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklists"
        element={
          <ProtectedRoute>
            <Checklists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklists/new"
        element={
          <ProtectedRoute roles={MANAGE}>
            <ChecklistEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklists/:id/edit"
        element={
          <ProtectedRoute roles={MANAGE}>
            <ChecklistEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute roles={ADMIN_ONLY}>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions"
        element={
          <ProtectedRoute>
            <Executions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions/go/:scheduleId"
        element={
          <ProtectedRoute>
            <ExecutionGo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions/:id"
        element={
          <ProtectedRoute>
            <ChecklistRunner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timeclock"
        element={
          <ProtectedRoute>
            <TimeClock />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timeclock/mirror"
        element={
          <ProtectedRoute>
            <TimeMirror />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timeclock/admin"
        element={
          <ProtectedRoute roles={MANAGE}>
            <TimeClockAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kiosk"
        element={
          <ProtectedRoute roles={["timeclock_kiosk"]}>
            <Kiosk />
          </ProtectedRoute>
        }
      />
      <Route
        path="/occurrences"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Occurrences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedules"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Schedules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Rules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/appcc"
        element={
          <ProtectedRoute>
            <APPCC />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audits"
        element={
          <ProtectedRoute roles={[...MANAGE, "auditor"]}>
            <Audits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Equipment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute roles={[...MANAGE, "auditor"]}>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sanitary"
        element={
          <ProtectedRoute roles={MANAGE}>
            <Sanitary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-consumptions"
        element={
          <ProtectedRoute roles={MANAGE}>
            <StockConsumptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-ranking"
        element={
          <ProtectedRoute roles={MANAGE}>
            <TeamRanking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trainings"
        element={
          <ProtectedRoute>
            <Trainings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/corporate"
        element={
          <ProtectedRoute roles={ADMIN_ONLY}>
            <Corporate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbot"
        element={
          <ProtectedRoute>
            <Chatbot />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/plans"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminSubscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminPayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/metrics"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminMetrics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminTickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feature-flags"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminFeatureFlags />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monitoring"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminMonitoring />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support-sessions"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminSupportSessions />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <PwaManager />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
